import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { userBrandsList } from '@/store/slices/casino-instance';

import { Toast } from '../../components';
import { ftuxSignal } from '../../features/ftux/signals';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { useToast } from '../../hooks/useToast';
import { buildCasinoSettings, CASINO_VARIANT_COUNT } from '../../mock-data/casino-settings';
import { WebsitePage } from '../website/components/WebsiteShell';
import { LogosSection, UploadLogoModal } from '../casino-settings/components';
import { localizeLogoLabel } from '../casino-settings/components/LogosSection/LogosSection';
import {
  resolveLogos,
  applyProvenance,
  logoSetSource,
  logoStepState,
  computeLogoEditWindow,
  unknownLogoEditWindow,
  type DisplayLogo,
} from '../casino-settings/logos';
import { cascadeMatters, rebuiltBy, PRIMARY_SLOT } from '../casino-settings/logo-api';
import { useLogoReplacement, type ReplacementOptions } from '../casino-settings/logo-replacement';
import { usePlayersRoster } from '../players/usePlayersRoster';
import { Page } from './logos.styles';
import { replaceSlotParam } from './replace-param';
import { useLogoDownloads } from './useLogoDownloads';

export type LogosPageProps = {
  styleText?: string;
  className?: string;
};

/**
 * Website → Logos (`/account/website/logos`): the casino's logo set, to download or replace.
 *
 * Moved here from Settings → Website Settings, which keeps the domains (and the in-progress casino type and
 * technical details). The section, the replace dialog and the logo modules still live in `../casino-settings`,
 * which creatives already reads the logo pipeline from; this page is only the orchestrator.
 *
 * The logo SET is real: read from casino-creation's own logo family, falling back to the brand's
 * `casino_data.details.logo` map until that lands. So is the logo-edit window that gates replacing them (the
 * registered-player count). REPLACING one is real too: the artwork is uploaded and a pipeline run is queued
 * to rebuild the family from it, which takes minutes rather than being instant (see
 * `../casino-settings/logo-replacement`). The schematic fallback set and the fallback casino name still come
 * from `buildCasinoSettings`, picked per brand by its list position (cycling, exactly like Home).
 */
const LogosPage = ({ styleText = '', className = '' }: LogosPageProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const brands = useAppSelector((state) => state.casinoInstance.brands, isEqual);
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand, isEqual);
  const brandsLoading = useAppSelector((state) => state.casinoInstance.loading);
  const brandsLoaded = useAppSelector((state) => state.casinoInstance.loaded);
  const brandsFailed = useAppSelector((state) => Boolean(state.casinoInstance.error));

  // MainLayout already loads the brand list app-wide; this guarded one-shot makes the page self-sufficient
  // if it's ever shown without that chrome, and is a no-op once loaded/loading.
  const requestedBrands = React.useRef(false);
  React.useEffect(() => {
    if (requestedBrands.current) return;
    requestedBrands.current = true;
    if (!brandsLoaded && !brandsLoading) dispatch(userBrandsList());
  }, [dispatch, brandsLoaded, brandsLoading]);

  // The brand currently on screen, readable ACROSS an await. `onLogoReplaced` spans a multi-second upload
  // during which the owner can switch casinos, and a value captured in the closure would still be the one
  // from the render that started it.
  const shownBrandRef = React.useRef<string | undefined>(undefined);
  shownBrandRef.current = displayBrand?.id;

  const variant = React.useMemo<number>(() => {
    if (!displayBrand) return 0;
    const idx = brands.findIndex((brand) => brand.id === displayBrand.id);
    return ((Math.max(0, idx) % CASINO_VARIANT_COUNT) + CASINO_VARIANT_COUNT) % CASINO_VARIANT_COUNT;
  }, [brands, displayBrand]);

  const data = React.useMemo(() => buildCasinoSettings(variant), [variant]);

  // The casino's name + logo set come from the active brand when it has them. The brand switcher reads the
  // same per-slot logo map (it just uses one slot); here every slot becomes a downloadable variant, the favicon
  // included (`logoSetSource`). `brandLogoSource` resolves that map from the brand's own `casino_data.details.logo`
  // first, falling back to the deployment record's `status_details.logoAssets.logos`. Brands with only a
  // single-URL / empty logo fall back to a schematic lockup set (see `resolveLogos`).
  const brandName = displayBrand?.casino_data?.details?.brand || data.casinoName;
  // The brand record carries the tenant id as a string; casino-creation keys everything by the number.
  // Anything unparseable means we cannot address the tenant at all, so the replace flow stays inert rather
  // than posting a NaN.
  const tenantId = React.useMemo(() => {
    const parsed = Number(displayBrand?.tenant_id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  }, [displayBrand?.tenant_id]);
  // The logo family, and any replacement run in flight against it. The family is read from casino-creation
  // (the pipeline's own source of truth, ahead of the brand record, which only catches up once the deployment
  // callback lands), and replacing queues a run there rather than writing anything locally.
  const {
    family,
    graph,
    loading: familyLoading,
    failed: familyFailed,
    reload: reloadFamily,
    pending: pendingRun,
    failure: runFailure,
    replace,
  } = useLogoReplacement(tenantId, brandName);
  // Prefer the family's own map; fall back to the brand record until it has loaded, so the logos are on
  // screen immediately rather than after a second round trip. Either way with that record's favicon.
  const logoSet = React.useMemo(
    () =>
      applyProvenance(
        resolveLogos(logoSetSource(family, displayBrand), data.logos, brandName),
        family,
        graph,
      ),
    [family, graph, displayBrand, data.logos, brandName],
  );

  // The placeholder is for a brand with NOTHING to show: `resolveLogos` falls back to schematic lockups that
  // read as a finished brand, which is misleading mid-deploy. Whether the brand's own map resolved is the
  // whole test; a logo step in flight is NOT. Replacing a logo repopulates the brand's deploy steps (a run
  // leaves `synthesize_base_logo` + `prepare_logo_assets` pending), so gating on it threw away a complete set
  // of real logos on a reload mid-run. That artwork is what the site is still serving and what is being
  // redrawn, so it stays on screen and the run is expressed through `updating` below instead.
  const logoStep = logoStepState(displayBrand);
  const logosReady = logoSet.source === 'brand';
  // No brand yet while the brand list is still coming in (or the selected brand is still being picked from it): there is
  // nothing to say "no logos" about, so the section holds its skeleton. An owner whose list loaded with no brand at all,
  // or whose list could not be read, gets the placeholder rather than a skeleton that never ends.
  const brandResolving = !displayBrand && !brandsFailed && (!brandsLoaded || brands.length > 0);
  const logosGenerating = logoStep === 'generating';

  // The slots the section draws its regeneration field over: ONLY the run this page is actually following,
  // live or resumed from storage. The brand record's deploy step is a load-time snapshot nothing on this
  // route refreshes, so once it went stale it would pin every logo to "Updating…" with Replace disabled for
  // the rest of the session. A signal that cannot be refreshed cannot answer "is something running right now".
  const updatingSlots = React.useMemo(() => pendingRun?.affects ?? [], [pendingRun]);

  // The logo-edit lock (the green/gold banner + whether the Replace controls are enabled) is REAL, and its
  // ONE input is the brand's registered-player count: there is no grace period, so a casino stays editable
  // until it has taken on players. That count is the roster the Players page pages in, reused here (cached
  // per brand, so it's free once either page has loaded it). `computeLogoEditWindow` resolves the 50-player
  // rule; see `../casino-settings/logos`.
  const roster = usePlayersRoster(displayBrand?.id);
  const editWindow = React.useMemo(
    () => (roster.registrationsFailed ? unknownLogoEditWindow() : computeLogoEditWindow(roster.data.length)),
    [roster.registrationsFailed, roster.data.length],
  );
  // Hold the section only while the roster is genuinely still LOADING. A roster whose registration half
  // FAILED never retries within a visit, so holding on it would leave the section in skeletons for good.
  // That case is an explicit verdict (`unknownLogoEditWindow`) instead: the logos render, and only replacing
  // is shut.
  const editWindowLoading = roster.loading;

  // Swapping in a real API means replacing this with a query hook of the same `{ data, loading }` shape.
  const logos = useMockEndpoint(data.logos);

  const { toast, show, dismiss } = useToast();
  const onToast = React.useCallback(
    (message: string, placeholders?: Record<string, string | number>) =>
      show(message, placeholders ? { placeholders } : undefined),
    [show],
  );

  // The downloads (one logo, or the whole set as a zip) are shared with the Website hub's Logos card.
  const { downloadLogo, downloadAll } = useLogoDownloads({ logoSet, brandName, notify: onToast });

  // The replace overlay is keyed by SLOT id, not by the logo object, so its target stays the live entry from
  // `logoSet` (it re-resolves as data lands) instead of a snapshot.
  const [replaceSlot, setReplaceSlot] = React.useState<string | null>(null);
  // Slot ids are shared across brands, so a brand switch must CLOSE the overlay rather than let it retarget
  // the same slot on the newly selected casino.
  React.useEffect(() => {
    setReplaceSlot(null);
  }, [displayBrand?.id]);

  // The `?replace=<slot>` deep link (the Website hub's "Replace logo"): read once the section is ready, opened only
  // where the Replace control itself would work (a slot this brand has, the edit window open, no run in flight), and
  // removed from the URL whatever the value, so a reload never reopens the upload. A brand without real logos has no
  // slot to open, and the link is removed all the same once the page has settled. The replace keeps the entry's
  // router state: it carries the hub's mark, which the back button pops back to the hub by. Declared after the
  // brand-switch reset above, so when both run in one commit the link wins. `logos.loading` is part of the section's
  // own readiness: on a fresh mount it holds the link past the first commit, whose `familyLoading` and `pendingRun`
  // are still the replacement hook's initial values (its read and the stored run's resume start in that same commit).
  const location = useLocation();
  const navigate = useNavigate();
  const replaceLinkHandled = React.useRef(false);
  React.useEffect(() => {
    if (replaceLinkHandled.current) return;
    if (logos.loading || familyLoading || editWindowLoading) return;
    replaceLinkHandled.current = true;
    const { slot, search } = replaceSlotParam(
      location.search,
      logosReady ? [logoSet.primary, ...logoSet.variants].map((logo) => logo.id) : [],
    );
    if (slot && editWindow.editable && !pendingRun) setReplaceSlot(slot);
    if (search !== location.search) {
      navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, state: location.state });
    }
  }, [
    logos.loading,
    familyLoading,
    logosReady,
    editWindowLoading,
    location,
    logoSet,
    editWindow.editable,
    pendingRun,
    navigate,
  ]);

  const replaceTarget = React.useMemo<DisplayLogo | null>(() => {
    if (!replaceSlot) return null;
    return [logoSet.primary, ...logoSet.variants].find((logo) => logo.id === replaceSlot) ?? null;
  }, [replaceSlot, logoSet]);

  /**
   * The logos a rebuild would redraw from `slot`, as slot ids, narrowed to the ones this brand actually has.
   * Comes from the pipeline's dependency graph, so the modal asks about a rebuild only where one is real.
   * The dialog counts them rather than naming them, so there is nothing to localise here.
   *
   * Asked on the PRIMARY logo only. The primary is the root the whole set derives from, so replacing it is a
   * rebrand and rebuilding the rest from it is the real question. A rebuild started from an intermediate
   * variant would redraw a branch out of a logo that is itself derived, which is not a choice worth putting
   * in front of an owner who came to change one variant, so those replacements change exactly the logo they
   * name (plus the favicon, which a short-name run redraws from it: see `runAffects`).
   */
  const rebuildsFor = React.useCallback(
    (slot: string): string[] => {
      // Keyed on the pipeline's base slot rather than on whichever logo happens to be showcased:
      // `logoSet.primary` is just the first slot the brand actually has, and a rebrand can only ever be
      // seeded from the base (it is what `logo_rebrand` means).
      if (slot !== PRIMARY_SLOT) return [];
      if (!cascadeMatters(graph, slot)) return [];
      const affected = new Set(rebuiltBy(graph, slot));
      return [logoSet.primary, ...logoSet.variants].filter((logo) => affected.has(logo.id)).map((logo) => logo.id);
    },
    [graph, logoSet],
  );

  const onReplace = React.useCallback((logo: DisplayLogo) => {
    setReplaceSlot(logo.id);
    // FTUX: pressing Replace logo ends the logo piece (its tour steps aside; the next piece waits in Spotlight).
    ftuxSignal('logo-replace-pressed');
  }, []);
  // The ONLY place the overlay is torn down. The dialog asks its overlay to close, the overlay plays the exit
  // and then calls this, so both replacing and cancelling slide out instead of blinking away mid-animation.
  const onReplaceClose = React.useCallback(() => setReplaceSlot(null), []);

  const onLogoReplaced = React.useCallback(
    async (imageDataUri: string, options: ReplacementOptions): Promise<boolean> => {
      if (!replaceSlot) return false;
      const startedFor = shownBrandRef.current;
      const label = localizeLogoLabel(replaceTarget?.label ?? '', t);
      const queued = await replace(replaceSlot, imageDataUri, options);
      if (!queued) return false;
      // Queued for the brand it was STARTED for, which may no longer be the one on screen: the upload can
      // run for seconds and the owner can switch casinos meanwhile. The run is real either way (so the dialog
      // still closes), but the confirmation must not land on a casino where nothing is happening.
      if (shownBrandRef.current !== startedFor) return true;
      // The run is queued, not finished: say so rather than claim the logo has changed. The section keeps
      // showing the CURRENT artwork until the new one actually exists.
      onToast(t('Updating {{label}}… this can take a few minutes.'), { label });
      return true;
    },
    [replace, replaceSlot, replaceTarget, onToast, t],
  );

  // The queue-time failure is reported by the dialog (it stays open with the reason). This covers the ones
  // that land after it has closed: a run accepted and then ended badly minutes later, and a run we simply
  // lost track of. `undefined` is the only value that means "nothing to report", so the ref is cleared on it;
  // keying purely on the failure's CONTENT would dedupe a genuine second failure of the same slot away.
  const reportedFailure = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (!runFailure) {
      reportedFailure.current = undefined;
      return;
    }
    const key = `${runFailure.slot}:${runFailure.reason}`;
    if (reportedFailure.current === key) return;
    reportedFailure.current = key;
    // Exhaustive on purpose: `reason` is a closed union, so the `never` case makes the compiler refuse a new
    // reason that nothing here reports.
    switch (runFailure.reason) {
      case 'queue':
        // The dialog is still open and shows the reason itself; saying it twice is worse.
        break;
      case 'run':
        onToast(t("That logo couldn't be updated. Your current logos are unchanged."));
        break;
      case 'lost':
        // Deliberately NOT phrased as a failure: the run was queued and is very likely still going, we just
        // stopped waiting. Calling it failed would send the owner to re-upload over a run that is about to land.
        onToast(t("We've lost track of that update. It may still be running, so check back in a minute."));
        break;
      case 'refresh':
        onToast(t('Your logos may have changed. Reload the page to see the current set.'));
        break;
      default: {
        // Never reached at runtime; the compile-time guard that a reason added to the union without a branch
        // here cannot ship silently.
        const unhandled: never = runFailure.reason;
        void unhandled;
      }
    }
  }, [runFailure, onToast, t]);

  return (
    <WebsitePage styleText={styleText} className={className}>
      <Page>
        <LogosSection
          primary={logoSet.primary}
          variants={logoSet.variants}
          window={editWindow}
          // `familyLoading` is the REAL fetch. Without it the only gate was a mock timer that settles well
          // before a proxied round trip, so a brand whose logos live only in the family flashed "No logo set
          // available yet" and then popped its real logos in.
          loading={logos.loading || brandResolving || familyLoading || (logosReady && editWindowLoading)}
          ready={logosReady}
          // A family read that failed, for a brand whose own record has no logos to fall back to: the error with a
          // retry. "No logo set available yet" is only for a read that answered with none.
          failed={familyFailed}
          onRetry={reloadFamily}
          generating={logosGenerating}
          updating={updatingSlots}
          onReplace={onReplace}
          onDownload={downloadLogo}
          onDownloadAll={downloadAll}
        />
      </Page>

      {replaceTarget ? (
        <UploadLogoModal
          casinoName={brandName}
          logo={replaceTarget}
          rebuilds={rebuildsFor(replaceTarget.id)}
          onClose={onReplaceClose}
          onReplaced={onLogoReplaced}
        />
      ) : null}

      <Toast toast={toast} onDismiss={dismiss} />
    </WebsitePage>
  );
};

export default LogosPage;
